import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Play, Target, TrendingUp, Loader2, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function PracticeAgainButton({
  // New props (from Results page)
  scenarioId,
  scenarioName,
  lastScore,
  onNavigate,
  // Legacy props (from session history)
  session,
  scenario,
  // Common props
  variant = 'primary',
  size = 'medium',
  showOptions = true
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { authFetch } = useAuth();

  // Normalize props - support both old and new prop patterns
  const resolvedScenarioId = scenarioId || scenario?.id || session?.scenario_id;
  const resolvedScore = lastScore || session?.overall_score;

  const handlePracticeAgain = async (mode = 'repeat') => {
    setLoading(true);
    setShowDropdown(false);

    try {
      // Call onNavigate callback if provided (for cleanup)
      if (onNavigate) {
        onNavigate();
      }

      if (mode === 'new' || !session?.id) {
        // Start fresh with the same scenario (or when no session available)
        navigate(`/scenario/${resolvedScenarioId}`);
      } else {
        // Create a repeat practice session linked to the original
        const response = await authFetch('/api/training/repeat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: session.id
          })
        });

        const data = await response.json();

        if (data.session) {
          // Navigate to the scenario with repeat context
          navigate(`/scenario/${resolvedScenarioId}`, {
            state: {
              repeatSession: data.session,
              originalScore: data.original_score,
              attemptNumber: data.attempt_number
            }
          });
        } else {
          // Fallback to simple navigation
          navigate(`/scenario/${resolvedScenarioId}`);
        }
      }
    } catch (error) {
      console.error('Error starting practice:', error);
      // Fallback to simple navigation on error
      navigate(`/scenario/${resolvedScenarioId}`);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2',
    large: 'px-6 py-3 text-lg'
  };

  const variantClasses = {
    primary: 'bg-foreground text-background hover:opacity-90',
    secondary: 'bg-muted hover:bg-muted text-foreground',
    success: 'bg-foreground text-background hover:opacity-90'
  };

  if (!showOptions) {
    return (
      <button
        onClick={() => handlePracticeAgain('repeat')}
        disabled={loading}
        className={`
          flex items-center gap-2 font-medium rounded-md transition-colors
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <RotateCcw className="w-5 h-5" />
        )}
        Practice Again
      </button>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <button
          onClick={() => handlePracticeAgain('repeat')}
          disabled={loading}
          className={`
            flex items-center gap-2 font-medium rounded-l-xl transition-colors
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <RotateCcw className="w-5 h-5" />
          )}
          Practice Again
        </button>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={loading}
          className={`
            p-2 font-medium rounded-r-xl border-l border-white/20 transition-colors
            ${variantClasses[variant]}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <AnimatePresence>
        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowDropdown(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute right-0 top-full mt-2 w-64 bg-card rounded-lg border border-border shadow-xl overflow-hidden z-50"
            >
              <div className="p-2 space-y-1">
                <button
                  onClick={() => handlePracticeAgain('repeat')}
                  className="w-full flex items-start gap-3 p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <TrendingUp className="w-5 h-5 text-primary-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Beat Your Score</p>
                    <p className="text-xs text-muted-foreground">
                      Track improvement from your previous {resolvedScore}%
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    if (onNavigate) onNavigate();
                    navigate(`/scenario/${resolvedScenarioId}`);
                  }}
                  className="w-full flex items-start gap-3 p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <Play className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Fresh Start</p>
                    <p className="text-xs text-muted-foreground">
                      Start a new session without tracking
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    if (onNavigate) onNavigate();
                    navigate(`/scenario/${resolvedScenarioId}?difficulty=harder`);
                  }}
                  className="w-full flex items-start gap-3 p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <Target className="w-5 h-5 text-orange-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Challenge Mode</p>
                    <p className="text-xs text-muted-foreground">
                      Same scenario, higher difficulty
                    </p>
                  </div>
                </button>
              </div>

              {session?.attempt_number && (
                <div className="px-4 py-2 bg-muted/50 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    This will be attempt #{(session.attempt_number || 0) + 1}
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
